import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getReferralStats, getUserReferrals, getUserReferredBy } from '../../actions/referralLedger';
import { Row, Col, Card, Button, Form, Alert, Spinner, Table, Badge } from "react-bootstrap";

const ReferralStats = (props) => {
    const [filters, setFilters] = useState({
        userId: '',
        startDate: '',
        endDate: ''
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        props.getReferralStats();
    }, []);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    const handleFilterChange = (e) => {
        setFilters({
            ...filters,
            [e.target.name]: e.target.value
        });
    };

    const handleFilterSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setError('');
            await props.getReferralStats(filters);
            setSuccess('Statistics updated successfully!');
        } catch (err) {
            setError('Failed to fetch statistics. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClearFilters = () => {
        setFilters({
            userId: '',
            startDate: '',
            endDate: ''
        });
        props.getReferralStats();
    };

    const handleGetUserReferrals = async (userId) => {
        try {
            setIsSubmitting(true);
            setError('');
            await props.getUserReferrals(userId);
            setSelectedUser('referrals');
            setSuccess('User referrals loaded successfully!');
        } catch (err) {
            setError('Failed to fetch user referrals. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGetUserReferredBy = async (userId) => {
        try {
            setIsSubmitting(true);
            setError('');
            await props.getUserReferredBy(userId);
            setSelectedUser('referredBy');
            setSuccess('User referred by data loaded successfully!');
        } catch (err) {
            setError('Failed to fetch user referred by data. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { variant: 'warning', text: 'Pending' },
            completed: { variant: 'success', text: 'Completed' },
            cancelled: { variant: 'danger', text: 'Cancelled' },
            expired: { variant: 'secondary', text: 'Expired' }
        };
        
        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const renderOverallStats = () => {
        if (!props.stats) return null;

        return (
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center border-primary h-100">
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-center mb-3">
                                <i className="fas fa-handshake fa-2x text-primary me-3"></i>
                                <div>
                                    <h3 className="text-primary mb-0">{props.stats.totalReferrals}</h3>
                                    <small className="text-muted">Total Referrals</small>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center border-success h-100">
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-center mb-3">
                                <i className="fas fa-check-circle fa-2x text-success me-3"></i>
                                <div>
                                    <h3 className="text-success mb-0">{props.stats.completedReferrals}</h3>
                                    <small className="text-muted">Completed</small>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center border-warning h-100">
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-center mb-3">
                                <i className="fas fa-clock fa-2x text-warning me-3"></i>
                                <div>
                                    <h3 className="text-warning mb-0">{props.stats.pendingReferrals}</h3>
                                    <small className="text-muted">Pending</small>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center border-info h-100">
                        <Card.Body>
                            <div className="d-flex align-items-center justify-content-center mb-3">
                                <i className="fas fa-gift fa-2x text-info me-3"></i>
                                <div>
                                    <h3 className="text-info mb-0">{props.stats.totalRewardCans}</h3>
                                    <small className="text-muted">Reward Cans</small>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    };

    const renderDetailedStats = () => {
        if (!props.stats) return null;

        return (
            <Row className="mb-4">
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-secondary text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-chart-pie me-2"></i>
                                Status Breakdown
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span>Completed</span>
                                <Badge bg="success" className="fs-6">{props.stats.completedReferrals}</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span>Pending</span>
                                <Badge bg="warning" className="fs-6">{props.stats.pendingReferrals}</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span>Expired</span>
                                <Badge bg="secondary" className="fs-6">{props.stats.expiredReferrals}</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span>Cancelled</span>
                                <Badge bg="danger" className="fs-6">{props.stats.cancelledReferrals}</Badge>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-info text-white">
                            <h5 className="mb-0">
                                <i className="fas fa-chart-line me-2"></i>
                                Performance Metrics
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <span>Completion Rate</span>
                                    <span className="fw-bold text-success">{props.stats.completionRate}%</span>
                                </div>
                                <div className="progress">
                                    <div 
                                        className="progress-bar bg-success" 
                                        style={{width: `${props.stats.completionRate}%`}}
                                    ></div>
                                </div>
                            </div>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>Total Reward Value</span>
                                    <span className="fw-bold text-info">₹{props.stats.totalRewardValue}</span>
                                </div>
                            </div>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>Total Reward Cans</span>
                                    <span className="fw-bold text-primary">{props.stats.totalRewardCans}</span>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    };

    const renderFilters = () => {
        return (
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">
                        <i className="fas fa-filter me-2"></i>
                        Filter Statistics
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleFilterSubmit}>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>User ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="userId"
                                        value={filters.userId}
                                        onChange={handleFilterChange}
                                        placeholder="Enter user ID"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={filters.startDate}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={filters.endDate}
                                        onChange={handleFilterChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="d-flex gap-2">
                            <Button type="submit" variant="primary" disabled={isSubmitting}>
                                {isSubmitting ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-search me-2"></i>
                                        Apply Filters
                                    </>
                                )}
                            </Button>
                            <Button type="button" variant="outline-secondary" onClick={handleClearFilters}>
                                <i className="fas fa-times me-2"></i>
                                Clear Filters
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        );
    };

    const renderUserActions = () => {
        return (
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0">
                        <i className="fas fa-user me-2"></i>
                        User-Specific Actions
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Get User Referrals</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter user ID"
                                            value={filters.userId}
                                            onChange={(e) => setFilters({...filters, userId: e.target.value})}
                                        />
                                        <Button 
                                            variant="info"
                                            onClick={() => handleGetUserReferrals(filters.userId)}
                                            disabled={!filters.userId || isSubmitting}
                                        >
                                            {isSubmitting ? <Spinner size="sm" /> : 'Get Referrals'}
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Get User Referred By</Form.Label>
                                    <div className="d-flex gap-2">
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter user ID"
                                            value={filters.userId}
                                            onChange={(e) => setFilters({...filters, userId: e.target.value})}
                                        />
                                        <Button 
                                            variant="success"
                                            onClick={() => handleGetUserReferredBy(filters.userId)}
                                            disabled={!filters.userId || isSubmitting}
                                        >
                                            {isSubmitting ? <Spinner size="sm" /> : 'Get Referred By'}
                                        </Button>
                                    </div>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>
        );
    };

    const renderUserReferrals = () => {
        if (selectedUser !== 'referrals' || !props.userReferrals || props.userReferrals.length === 0) return null;

        return (
            <Card className="mb-4">
                <Card.Header className="bg-info text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-user-friends me-2"></i>
                        User Referrals
                    </h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table striped hover responsive className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Referee</th>
                                <th>Referral ID</th>
                                <th>Status</th>
                                <th>Reward</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.userReferrals.map((referral) => (
                                <tr key={referral._id}>
                                    <td>
                                        <div>
                                            <div className="fw-bold">{referral.referee?.name || 'N/A'}</div>
                                            <small className="text-muted">{referral.referee?.phone || 'N/A'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <code>{referral.referralId}</code>
                                    </td>
                                    <td>
                                        {getStatusBadge(referral.status)}
                                    </td>
                                    <td>
                                        <div>
                                            <div className="fw-bold">{referral.reward?.totalValue || 0} cans</div>
                                            <small className="text-muted">
                                                R: {referral.reward?.referrerCans || 0} | 
                                                E: {referral.reward?.refereeCans || 0}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        {formatDate(referral.createdAt)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    };

    const renderUserReferredBy = () => {
        if (selectedUser !== 'referredBy' || !props.userReferredBy || props.userReferredBy.length === 0) return null;

        return (
            <Card className="mb-4">
                <Card.Header className="bg-success text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-user-plus me-2"></i>
                        User Referred By
                    </h5>
                </Card.Header>
                <Card.Body className="p-0">
                    <Table striped hover responsive className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Referrer</th>
                                <th>Referral ID</th>
                                <th>Status</th>
                                <th>Reward</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.userReferredBy.map((referral) => (
                                <tr key={referral._id}>
                                    <td>
                                        <div>
                                            <div className="fw-bold">{referral.referrer?.name || 'N/A'}</div>
                                            <small className="text-muted">{referral.referrer?.phone || 'N/A'}</small>
                                        </div>
                                    </td>
                                    <td>
                                        <code>{referral.referralId}</code>
                                    </td>
                                    <td>
                                        {getStatusBadge(referral.status)}
                                    </td>
                                    <td>
                                        <div>
                                            <div className="fw-bold">{referral.reward?.totalValue || 0} cans</div>
                                            <small className="text-muted">
                                                R: {referral.reward?.referrerCans || 0} | 
                                                E: {referral.reward?.refereeCans || 0}
                                            </small>
                                        </div>
                                    </td>
                                    <td>
                                        {formatDate(referral.createdAt)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>
        );
    };

    if (props.loading) {
        return (
            <div className="d-flex justify-content-center align-items-center loading-container" style={{minHeight: '400px'}}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading referral statistics...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="mb-4">
                    <h2 className="text-primary mb-1">
                        <i className="fas fa-chart-bar me-2"></i>
                        Referral Statistics
                    </h2>
                    <p className="text-muted mb-0">
                        View comprehensive referral program statistics and analytics
                    </p>
                </div>

                {/* Alert Messages */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                        <i className="fas fa-check-circle me-2"></i>
                        {success}
                    </Alert>
                )}

                {/* Overall Statistics */}
                {renderOverallStats()}

                {/* Detailed Statistics */}
                {renderDetailedStats()}

                {/* Filters */}
                {renderFilters()}

                {/* User Actions */}
                {renderUserActions()}

                {/* User Referrals */}
                {renderUserReferrals()}

                {/* User Referred By */}
                {renderUserReferredBy()}
            </div>
        </Fragment>
    );
};

ReferralStats.propTypes = {
    getReferralStats: PropTypes.func.isRequired,
    getUserReferrals: PropTypes.func.isRequired,
    getUserReferredBy: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    stats: PropTypes.object,
    userReferrals: PropTypes.array,
    userReferredBy: PropTypes.array
};

const mapStateToProps = state => ({
    loading: state.referralLedger.loading,
    stats: state.referralLedger.stats,
    userReferrals: state.referralLedger.userReferrals,
    userReferredBy: state.referralLedger.userReferredBy
});

export default connect(mapStateToProps, { 
    getReferralStats, 
    getUserReferrals, 
    getUserReferredBy 
})(ReferralStats);
